// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Categories  (route /categories: the directory; ?c=all|fish|shellfish|premium|prawns|crabs-lobsters|squid shows a list)
// With no ?c= the page is the category directory (components/categories/CategoryDirectory, groups in data/categoryGroups.ts); a group tile or chip opens the list below.
// Browse the catch by category: category chips with counts, the category's heading and item count, a Cut filter and a
// Sort control, a note on cut prep pricing, and large catch tiles whose Add / Edit buttons open the same pack-size +
// cut sheet as the menu (hooks/usePackSheet → components/menu/PackSheet). Everything is real data:
//   • categories, items, prices, tags → data/seafood.ts;  cuts and their prep fees → data/cuts.ts.
//   • Sort options are the ones the data supports (menu order, price, name) — there is no "popularity" or rating data.
//   • The Cut filter lists only cuts that some catch in the chosen category actually offers.
// Deliberately NOT here (nothing in the system supports it): "in stock only" (no stock data), ratings/reviews, "landing
// time" / "N items today" claims, imported species (the menu has none), savings/strike-through prices, chilled-chain
// promises. Search-engine friendly: filters live in the URL (?c=…&cut=…&sort=…) so a filtered view can be shared.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Fish, LayoutGrid, Scissors, Shell, SlidersHorizontal, Sparkles, X } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import CategoryDirectory from "../components/categories/CategoryDirectory";
import CartBar from "../components/menu/CartBar";
import PackSheet from "../components/menu/PackSheet";
import ProductCard from "../components/menu/ProductCard";
import PageTransition from "../components/PageTransition";
import { CATEGORY_GROUPS } from "../data/categoryGroups";
import { cutsFor, prepLabel, type CutOption } from "../data/cuts";
import { seafoodItems, type SeafoodItem } from "../data/seafood";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePackSheet from "../hooks/usePackSheet";
import { parsePriceToNumber } from "../utils/priceFormat";

type SortKey = "menu" | "price-asc" | "price-desc" | "name";

const CATEGORIES: { slug: string; label: string; heading: string; icon: typeof Fish }[] = [
  { slug: "all", label: "All", heading: "The Whole Catch", icon: LayoutGrid },
  { slug: "fish", label: "Fish", heading: "Fresh Fish", icon: Fish },
  { slug: "shellfish", label: "Shellfish", heading: "Shellfish", icon: Shell },
  { slug: "premium", label: "Premium", heading: "Premium Catch", icon: Sparkles },
];

const SORTS: { key: SortKey; label: string }[] = [
  { key: "menu", label: "Menu order" },
  { key: "price-asc", label: "Price: low to high" },
  { key: "price-desc", label: "Price: high to low" },
  { key: "name", label: "Name: A to Z" },
];

/** The catches in a category chip or directory group (`all` = everything). */
const inCategory = (slug: string) => (slug === "all" ? seafoodItems : (CATEGORY_GROUPS.find((group) => group.slug === slug)?.items ?? seafoodItems));

/** Cuts offered by at least one of these catches, one entry per cut NAME (e.g. both "Curry Cut"s are one filter). */
function cutsOffered(items: SeafoodItem[]): CutOption[] {
  const byName = new Map<string, CutOption>();
  for (const item of items) for (const cut of cutsFor(item)) if (!byName.has(cut.name)) byName.set(cut.name, cut);
  return [...byName.values()];
}

const shortName = (item: SeafoodItem) => item.name.split(" (")[0];

export default function Categories() {
  useDocumentTitle("Categories");
  const [params, setParams] = useSearchParams();
  const sheet = usePackSheet();

  // No (or an unknown) ?c= shows the directory; `all`, a chip slug or a directory group slug shows that list.
  const requested = params.get("c");
  const group = CATEGORY_GROUPS.find((g) => g.slug === requested);
  const directory = !group && requested !== "all";
  const active = CATEGORIES.find((c) => c.slug === requested) ?? (group ? { slug: group.slug, label: group.title, heading: group.title, icon: Fish } : CATEGORIES[0]);
  const sort = (SORTS.find((s) => s.key === params.get("sort"))?.key ?? "menu") as SortKey;

  const inActive = useMemo(() => inCategory(active.slug), [active.slug]);
  const cutChoices = useMemo(() => cutsOffered(inActive), [inActive]);
  // A cut that this category doesn't offer (e.g. from an old link) is ignored rather than showing an empty page.
  const cutFilter = cutChoices.find((cut) => cut.name === params.get("cut"))?.name ?? "";

  const items = useMemo(() => {
    const filtered = cutFilter ? inActive.filter((item) => cutsFor(item).some((cut) => cut.name === cutFilter)) : inActive;
    const sorted = [...filtered];
    if (sort === "price-asc") sorted.sort((a, b) => parsePriceToNumber(a.price) - parsePriceToNumber(b.price));
    else if (sort === "price-desc") sorted.sort((a, b) => parsePriceToNumber(b.price) - parsePriceToNumber(a.price));
    else if (sort === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
  }, [inActive, cutFilter, sort]);

  /** Update one URL parameter, dropping it when it is back to its default; keeps the others. */
  const setParam = (key: string, value: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const chooseCategory = (slug: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("c", slug);
        next.delete("cut"); // cut choices differ by category
        return next;
      },
      { replace: true },
    );

  const filtersOn = cutFilter !== "" || sort !== "menu";
  const paid = cutsOffered(seafoodItems).filter((cut) => cut.prepFeePerKg > 0);

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-28 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />

        <main className="mx-auto flex max-w-6xl flex-col gap-4 px-4 pt-2">
          <header>
            <h1 className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50 sm:text-4xl">Categories</h1>
            {!directory && (
              <Link to="/categories" className="mt-1 inline-block text-sm font-semibold text-teal-700 underline underline-offset-2 dark:text-teal-300">
                ← All categories
              </Link>
            )}
            <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">
              Browse the catch by type. Prefer a search? Try the <Link to="/menu" className="font-semibold text-teal-700 underline underline-offset-2 dark:text-teal-300">full menu</Link>.
            </p>
          </header>

          <div role="group" aria-label="Category" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
            {CATEGORIES.map(({ slug, label, icon: Icon }) => (
              <button
                key={slug}
                type="button"
                aria-pressed={!directory && active.slug === slug}
                onClick={() => chooseCategory(slug)}
                className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
                  (!directory && active.slug === slug) ? "bg-ocean-800 text-white" : "bg-white text-ocean-900 ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10"
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
                <span className={`rounded-full px-2 text-xs ${(!directory && active.slug === slug) ? "bg-white/20" : "bg-ocean-700/10 dark:bg-white/10"}`}>{inCategory(slug).length}</span>
              </button>
            ))}
          </div>

          {directory ? (
            <CategoryDirectory />
          ) : (
            <>
          <section aria-labelledby="category-heading" className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 id="category-heading" className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">{active.heading}</h2>
              <span className="rounded-full bg-teal-100 px-3 py-1 text-xs font-bold text-teal-700">
                {inActive.length} {inActive.length === 1 ? "item" : "items"}
              </span>
            </div>
            <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">{inActive.map(shortName).join(", ")}.</p>
          </section>

          <div className="flex flex-wrap items-end gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
            <SlidersHorizontal className="mb-3 hidden h-5 w-5 shrink-0 text-ocean-800/70 sm:block dark:text-foam-100/70" aria-hidden="true" />
            <div className="min-w-40 flex-1">
              <label htmlFor="cat-cut" className="text-xs font-bold uppercase tracking-wide text-ocean-800/80 dark:text-foam-100/75">Cut</label>
              <select
                id="cat-cut"
                value={cutFilter}
                onChange={(e) => setParam("cut", e.target.value)}
                className="mt-1 h-11 w-full rounded-lg bg-ocean-700/5 px-3 text-sm font-semibold text-ocean-950 outline-none focus:ring-2 focus:ring-teal-600 dark:bg-white/10 dark:text-foam-50"
              >
                <option value="">Any cut</option>
                {cutChoices.map((cut) => (
                  <option key={cut.name} value={cut.name}>{cut.name}</option>
                ))}
              </select>
            </div>
            <div className="min-w-40 flex-1">
              <label htmlFor="cat-sort" className="text-xs font-bold uppercase tracking-wide text-ocean-800/80 dark:text-foam-100/75">Sort</label>
              <select
                id="cat-sort"
                value={sort}
                onChange={(e) => setParam("sort", e.target.value === "menu" ? "" : e.target.value)}
                className="mt-1 h-11 w-full rounded-lg bg-ocean-700/5 px-3 text-sm font-semibold text-ocean-950 outline-none focus:ring-2 focus:ring-teal-600 dark:bg-white/10 dark:text-foam-50"
              >
                {SORTS.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
            {filtersOn && (
              <button
                type="button"
                onClick={() => setParams((current) => { const next = new URLSearchParams(current); next.delete("cut"); next.delete("sort"); return next; }, { replace: true })}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-full px-4 text-sm font-bold text-ocean-800 hover:bg-ocean-700/10 dark:text-foam-100"
              >
                <X className="h-4 w-4" aria-hidden="true" /> Clear filters
              </button>
            )}
          </div>

          <aside aria-label="About cuts" className="flex items-start gap-3 rounded-2xl bg-ocean-800 p-4 text-white">
            <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15"><Scissors className="h-5 w-5" /></span>
            <div>
              <p className="font-display text-lg font-bold leading-tight">Your cut, your way</p>
              <p className="text-sm text-foam-100/90">
                Choose the cut for each catch when you add it — most are free
                {paid.length > 0 && <>; {paid.map((cut) => `${cut.name} is ${prepLabel(cut)}`).join(", ")}</>}.
              </p>
            </div>
          </aside>

          <p role="status" className="text-sm text-ocean-800/80 dark:text-foam-100/75">
            {cutFilter ? `${items.length} of ${inActive.length} ${inActive.length === 1 ? "item offers" : "items offer"} “${cutFilter}”` : `Showing ${items.length} ${items.length === 1 ? "item" : "items"}`}
          </p>

          {items.length === 0 ? (
            <div className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-ocean-900">
              <p className="font-display text-lg font-bold">Nothing matches those filters</p>
              <button
                type="button"
                onClick={() => setParams({}, { replace: true })}
                className="mt-4 min-h-11 rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700"
              >
                Reset everything
              </button>
            </div>
          ) : (
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((item) => (
                <ProductCard key={item.id} variant="tile" {...sheet.cardProps(item)} />
              ))}
            </ul>
          )}

            </>
          )}

          <p className="mt-2 text-center text-xs text-ocean-800/70 dark:text-foam-100/65">
            Species photography courtesy of Wikimedia Commons contributors (CC BY / CC BY-SA). Hover a photo for credit.
          </p>
        </main>

        <PackSheet {...sheet.sheetProps} />
        <CartBar />
      </div>
    </PageTransition>
  );
}
